import { CheckCircle2, Lock } from 'lucide-react';
import type { ActivityStep } from '../classroomApi';
import { optionLetter } from './ChoiceResponse';

// /class live redesign (2026-09-11) — "have I answered?" is one of the three
// questions a student must be able to answer without hunting, and the old
// treatment ("Answer recorded." in 12 px green under a button) failed it: in
// a room of 77 people, half of them on a phone at arm's length, a thin green
// line reads as decoration. This is a band: an icon, a heading at the body
// text size, and — the part that actually settles the question — the
// student's OWN answer echoed back. "Answer recorded" says the server heard
// something; "You answered: 6" says it heard the right thing.
//
// Colour never carries the meaning alone (same rule as the run sheet's
// StateBadge): the words say "recorded", the tick and the border say it
// again in green. Text is --ws-ink/--ws-ink-2 on --ws-success-quiet, not
// --ws-success on --ws-success-quiet — that pair is ~2.9:1 and fails AA in
// the light themes (design §3.2). The tick is decorative and labelled by the
// text beside it.

/** The student's recorded answer as one short human string, or null when the
 *  payload shape isn't one this feature renders (never throws on an
 *  unexpected payload — the receipt simply drops the echo line). */
export function describeAnswer(
  step: ActivityStep,
  payload: Record<string, unknown> | undefined,
): string | null {
  if (!payload) return null;

  const widget = payload.widget as { display?: unknown } | undefined;
  if (widget && typeof widget.display === 'string' && widget.display.trim()) return widget.display;

  if (typeof payload.choice === 'number') {
    const text = step.options?.[payload.choice];
    if (text === undefined) return null;
    return `${optionLetter(payload.choice)} · ${text}`;
  }

  if (Array.isArray(payload.choices)) {
    const picked = payload.choices
      .filter((i): i is number => typeof i === 'number')
      .map((i) => step.options?.[i])
      .filter((t): t is string => typeof t === 'string');
    return picked.length > 0 ? picked.join(', ') : null;
  }

  if (typeof payload.value === 'number') return String(payload.value);
  if (typeof payload.text === 'string' && payload.text.trim()) return payload.text;

  return null;
}

interface AnswerReceiptProps {
  /** The echo line — omitted when the payload can't be described. */
  answer: string | null;
  /** False once the instructor has closed the vote. */
  canChange: boolean;
}

export function AnswerReceipt({ answer, canChange }: AnswerReceiptProps) {
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-lg border-2 border-ws-success bg-ws-success-quiet px-4 py-3"
    >
      <CheckCircle2 size={22} className="mt-0.5 shrink-0 text-ws-success" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-base font-semibold text-ws-ink">Answer recorded.</p>
        {answer && (
          <p className="mt-0.5 break-words text-sm text-ws-ink-2">
            You answered: <span className="font-semibold text-ws-ink">{answer}</span>
          </p>
        )}
        {canChange && (
          <p className="mt-1 text-xs text-ws-ink-3">
            You can change it until your instructor closes the vote.
          </p>
        )}
      </div>
    </div>
  );
}

/** WIP-011 (design §1, §4.3) — the vote is closed server-side too, so the
 *  controls come down entirely rather than staying up to fail on tap. Same
 *  band weight as the receipt: whatever state the student is in, it is the
 *  thing under the question, not a footnote. */
export function VotingClosedNotice({ answer }: { answer: string | null }) {
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-lg border border-ws-border-strong bg-ws-surface-2 px-4 py-3"
    >
      <Lock size={20} className="mt-0.5 shrink-0 text-ws-ink-3" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-base font-semibold text-ws-ink">Voting closed — look at the screen.</p>
        {answer && (
          <p className="mt-0.5 break-words text-sm text-ws-ink-2">
            You answered: <span className="font-semibold text-ws-ink">{answer}</span>
          </p>
        )}
      </div>
    </div>
  );
}
